import React, { Fragment } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import moment from 'moment';
import DatePicker from '../../shared/components/DatePicker';
import BookingSteps from '../../shared/components/BookingSteps';
import {
  MapPinIcon,
  CalendarDaysIcon,
  TicketIcon,
  TruckIcon,
  TagIcon,
  MinusIcon,
  PlusIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

import * as API from '../../api';
import { cn } from '../../shared/utils/cn';
import ConfirmAuthSheet from './ConfirmAuthSheet';

/**
 * Booking page — premium redesign.
 *
 * IMPORTANT: all booking state, the price math (totalPrice), coupon check,
 * placesAvailable, validation (handleDesableButton) and the submit handoff
 * (localStorage 'order_booking' -> /checkout) are preserved verbatim from the
 * legacy page. Only the presentation changed. MUI date pickers are kept for
 * the date logic but restyled.
 */

const TIME_SLOTS = [
  '08h00 : 10h00',
  '10h00 : 12h00',
  '12h00 : 14h00',
  '14h00 : 16h00',
  '16h00 : 18h00',
  '18h00 : 20h00',
  '20h00 : 22h00',
];

const Booking = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [gift] = React.useState(JSON.parse(localStorage.getItem('gift')) || {});
  const [event] = React.useState(JSON.parse(localStorage.getItem('order')) || {});
  const [placesAvailable, setPlacesAvailable] = React.useState(event.places_available);

  const [prices, setPrices] = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [selectedTime, setSelectedTime] = React.useState('');

  const [cityStart, setCityStart] = React.useState(null);
  const [roader, setRoader] = React.useState(null);

  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [settings, setSettings] = React.useState({});
  const [roaders, setRoaders] = React.useState([]);

  const [couponName, setCouponName] = React.useState('');
  const [coupon, setCoupon] = React.useState({});
  const [couponHelper, setCouponHelper] = React.useState('');
  const [loadingCoupon, setLoadingCoupon] = React.useState(false);
  const [addCoupon, setAddCoupon] = React.useState(false);

  // TAF-34: the confirm/auth step now happens inline on this page.
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [orderBooking, setOrderBooking] = React.useState(null);

  const [check_in, setCheck_in] = React.useState(new Date().toISOString().slice(0, 16));
  const [check_out, setCheck_out] = React.useState(
    new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
  );

  // ---- logic (preserved) ---------------------------------------------------

  const totalPrice = (_prices, undescount = false) => {
    let total = 0;
    let _total = total;
    _prices.forEach((price) => {
      total = total + price.total_price;
    });
    if (undescount) {
      let _coupon = coupon.value ?? 0;
      _total = total - (total / 100) * _coupon;
    } else {
      _total = total;
    }
    return _total;
  };

  const handleChangeCheckbox = (item) => {
    let exept = prices.filter((p) => p.id !== item.id);
    item.status = !item.status;
    item.qt = item.status ? 1 : 0;
    item.total_price = item.price * item.qt;
    setPrices([item, ...exept]);
  };

  const handleChangeQt = (e, item, qt = null) => {
    let nQt = qt ?? e.target.value;
    let exept = prices.filter((p) => p.id !== item.id);
    item.qt = nQt < 1 ? '' : nQt;
    item.total_price = item.price * item.qt;
    setPrices([item, ...exept]);
  };

  const checkCoupon = (form) => {
    setCoupon({});
    setLoadingCoupon(true);
    API.CheckCoupon(form)
      .then((res) => {
        setCoupon(res.coupon);
        setCouponHelper(t('booking.couponSuccess', { value: res.coupon.value }));
      })
      .catch((err) => {
        setCouponHelper(err?.data?.errors || '—');
      })
      .then(() => setLoadingCoupon(false));
  };

  const submitBooking = async () => {
    let d = new Date(selectedDate);
    let date =
      new Intl.DateTimeFormat('fr', { day: '2-digit' }).format(d) +
      '/' +
      new Intl.DateTimeFormat('fr', { month: '2-digit' }).format(d) +
      '/' +
      new Intl.DateTimeFormat('fr', { year: 'numeric' }).format(d);

    let _prices = [];
    await prices.forEach((price) => {
      if (price.qt > 0 && price.status === true) {
        _prices.push(price);
      }
    });

    let _form = {
      event: event,
      date_activity: date,
      time_activity: selectedTime,
      prices: _prices,
      city: cityStart,
      roader: roader,
      roaders: roaders,
      settings: settings,
      price_total: totalPrice(prices, false),
      price_for_pay: (totalPrice(prices, false) / 100) * (100 - event.percentage_paid),
      price_coupon: {
        total: totalPrice(prices, true),
        for_pay: (totalPrice(prices, true) / 100) * (100 - event.percentage_paid),
      },
      coupon,
      sejour_dates: { check_in: check_in, check_out: check_out },
    };
    // TAF-34: instead of routing to /checkout, open the inline confirm/auth
    // sheet. It persists order_booking and places the order -> /thankyou.
    setOrderBooking(_form);
    setConfirmOpen(true);
  };

  const validateSejourDates = () =>
    check_in !== '' && check_out !== '' && new Date(check_out) > new Date(check_in);

  const handleCheckInDateChange = (date) => {
    setCheck_in(date);
    handleSejourQtChange(date, check_in);
  };
  const handleCheckOutDateChange = (date) => {
    setCheck_out(date);
    handleSejourQtChange(check_in, date);
  };
  const handleSejourQtChange = (date_check_in, date_check_out) => {
    let newQt = 1;
    if (date_check_in !== '' && date_check_out !== '') {
      var date1 = new Date(date_check_out);
      var date2 = new Date(date_check_in);
      var differenceInMilliseconds = date1 - date2;
      var differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
      newQt = Math.floor(differenceInDays);
      newQt = newQt <= 0 ? 1 : newQt;
    }
    let newPrices = [];
    prices.forEach((price) => {
      price.qt = newQt;
      price.total_price = price.total_price * newQt;
      newPrices.push(price);
    });
    setPrices(newPrices);
  };

  React.useEffect(() => {
    if (!Object.keys(event).length) {
      history.replace('/');
      return;
    }
    if (Object.keys(gift).length && gift.gift_email) {
      event.percentage_paid = 100;
    }
    document.title = API.APP_NAME + ' - Booking - ' + event.title;

    let _prices = [];
    event.prices.forEach((item) => {
      _prices.push({
        ...item,
        status: parseInt(item.default) ? true : false,
        qt: item.default,
        total_price: item.price * item.default,
      });
    });
    setPrices(_prices);

    setLoadingSettings(true);
    API.Settings()
      .then((res) => {
        setSettings(res.settings);
        setRoaders(res.roaders);
      })
      .then(() => setLoadingSettings(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [0]);

  React.useEffect(() => {
    let total_places = 0;
    prices.forEach((price) => {
      total_places = parseInt(total_places) + parseInt(price.qt);
    });
    setPlacesAvailable(event.places_available - total_places);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  const handleDesableButton = () => {
    if (event.type == 'Event' && !cityStart) return true;
    if (event.type !== 'Event' && event.type != 'Sejour' && parseInt(event.hour_status) && !selectedTime)
      return true;
    if (!totalPrice(prices)) return true;
    if (event.type == 'Event' && parseInt(event.covoiturage) && !roader) return true;
    if (couponName && !Object.keys(coupon).length) return true;
    if (event.type == 'Sejour' && (!check_in || !check_out || !validateSejourDates())) return true;
    return false;
  };

  if (!Object.keys(event).length) return null;

  const isSejour = event.type === 'Sejour';
  const sortedPrices = [...prices].sort((a, b) => (a.name > b.name ? 1 : -1));
  const advance =
    totalPrice(prices, true) - (totalPrice(prices, true) / 100) * (100 - event.percentage_paid);
  // percentage_paid can be 100 (pay the full amount online, not a partial
  // deposit) — same distinction thankyou.js makes, so the banner doesn't
  // call a full payment a "deposit".
  const isFullOnlinePayment = parseFloat(event.percentage_paid) >= 100;

  // ---- render --------------------------------------------------------------

  return (
    <div className="bg-surface-muted pb-28 lg:pb-12">
      {/* Header */}
      <header className="border-b border-slate-100 bg-surface">
        <div className="mx-auto w-full max-w-container px-4 py-7 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-ink-subtle" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-500">
              {t('booking.breadcrumbHome')}
            </Link>
            <ChevronRightIcon className="h-3 w-3 rtl:rotate-180" />
            <Link to={`/event/${event.id}`} className="max-w-[200px] truncate hover:text-brand-500">
              {event.title}
            </Link>
            <ChevronRightIcon className="h-3 w-3 rtl:rotate-180" />
            <span className="text-ink-muted">{t('booking.stepDetails')}</span>
          </nav>
          <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            {t('booking.title')}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">{t('booking.subtitle')}</p>
          <BookingSteps active="details" hasDeposit={advance > 0} />
        </div>
      </header>

      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* ---- Left: form ---- */}
          <div className="space-y-6">
            {/* Date / type-specific block */}
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
              <div className="mb-4 flex items-center gap-2">
                <CalendarDaysIcon className="h-5 w-5 text-brand-500" />
                <h2 className="font-display text-lg font-semibold text-ink">
                  {event.title}
                </h2>
              </div>

              {/* Activity: date + optional time */}
              {event.type === 'Activitie' ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t('booking.chooseDate')}>
                    <DatePicker
                      value={selectedDate}
                      minDate={new Date()}
                      onChange={(date) => setSelectedDate(date)}
                      placeholder={t('booking.chooseDate')}
                    />
                  </Field>
                  {parseInt(event.hour_status) ? (
                    <Field label={t('booking.chooseTime')}>
                      <SelectBox
                        value={selectedTime}
                        onChange={setSelectedTime}
                        placeholder={t('booking.chooseTime')}
                        options={TIME_SLOTS.map((s) => ({ value: s, label: s }))}
                      />
                    </Field>
                  ) : null}
                </div>
              ) : null}

              {/* Trip: fixed dates + places */}
              {event.type === 'Event' ? (
                <div className="grid grid-cols-3 gap-4">
                  <Stat
                    label={t('booking.dateDepart')}
                    value={new Date(event.date_depart).toLocaleDateString('fr-FR')}
                  />
                  <Stat
                    label={t('booking.dateEnd')}
                    value={new Date(event.date_end).toLocaleDateString('fr-FR')}
                  />
                  <Stat
                    label={t('booking.placesAvailable')}
                    value={placesAvailable}
                    danger={placesAvailable <= 0}
                  />
                </div>
              ) : null}

              {/* Sejour: check-in / check-out */}
              {isSejour ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t('booking.checkIn')}>
                    <DatePicker
                      value={check_in}
                      minDate={new Date()}
                      onChange={(date) => handleCheckInDateChange(date)}
                      isDateDisabled={(date) => sejourDisable(date, event)}
                      placeholder={t('booking.checkIn')}
                    />
                  </Field>
                  <Field label={t('booking.checkOut')}>
                    <DatePicker
                      value={check_out}
                      minDate={new Date()}
                      onChange={(date) => handleCheckOutDateChange(date)}
                      isDateDisabled={(date) => sejourDisable(date, event)}
                      placeholder={t('booking.checkOut')}
                    />
                  </Field>
                </div>
              ) : null}
            </section>

            {/* Tickets / prices */}
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
              <div className="mb-4 flex items-center gap-2">
                <TicketIcon className="h-5 w-5 text-brand-500" />
                <div>
                  <h2 className="font-display text-lg font-semibold text-ink">
                    {t('booking.ticketsTitle')}
                  </h2>
                  <p className="text-xs text-ink-subtle">{t('booking.ticketsSubtitle')}</p>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                {sortedPrices.map((item) => (
                  <PriceRow
                    key={item.id}
                    item={item}
                    isSejour={isSejour}
                    onToggle={() => handleChangeCheckbox(item)}
                    onQt={(e, qt) => handleChangeQt(e, item, qt)}
                    t={t}
                  />
                ))}
              </div>
            </section>

            {/* Trip: departure city + carpool */}
            {event.type === 'Event' ? (
              <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
                <div className="space-y-5">
                  <Field
                    label={t('booking.departureCity')}
                    hint={t('booking.departureCityHint')}
                    icon={MapPinIcon}
                  >
                    <SelectBox
                      value={cityStart}
                      onChange={setCityStart}
                      placeholder={t('booking.departureCityPlaceholder')}
                      options={(event.gathering || []).map((g) => ({
                        value: g.gathering,
                        label: g.gathering,
                      }))}
                    />
                  </Field>

                  {parseInt(event.covoiturage) ? (
                    <Field
                      label={t('booking.carpool')}
                      hint={t('booking.carpoolHint')}
                      icon={TruckIcon}
                    >
                      <SelectBox
                        value={roader}
                        onChange={(v) => setRoader(v)}
                        placeholder={t('booking.carpoolPlaceholder')}
                        loading={loadingSettings}
                        options={roaders.map((r) => ({ value: r.id, label: r.name }))}
                      />
                    </Field>
                  ) : null}
                </div>
              </section>
            ) : null}
          </div>

          {/* ---- Right: sticky summary ---- */}
          <aside>
            <div className="lg:sticky lg:top-24">
              <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card">
                <div className="border-b border-slate-100 px-5 py-4">
                  <h3 className="font-display text-base font-semibold text-ink">
                    {t('booking.summaryTitle')}
                  </h3>
                </div>

                <div className="space-y-3 px-5 py-4 text-sm">
                  <p className="font-semibold text-ink">{event.title}</p>

                  {cityStart ? (
                    <SummaryLine label={t('booking.departureCity')} value={cityStart} />
                  ) : null}

                  <ul className="space-y-1">
                    {prices
                      .filter((p) => p.qt > 0)
                      .map((item, i) => (
                        <li key={i} className="flex items-center justify-between text-ink-muted">
                          <span>
                            {item.qt} × {item.name}
                          </span>
                          <bdi className="font-medium text-ink">
                            {API.priceFormat(item.total_price)}
                          </bdi>
                        </li>
                      ))}
                  </ul>

                  {isSejour ? (
                    <p className="text-xs text-ink-subtle">
                      {t('booking.summaryGuests', { count: event.capacitie })}
                    </p>
                  ) : null}

                  {event.covoiturage == '1' && roader ? (
                    <SummaryLine
                      label={t('booking.carpool')}
                      value={roaders.find((r) => r.id == roader)?.name}
                    />
                  ) : null}

                  {Object.keys(coupon).length ? (
                    <div className="flex items-center justify-between text-emerald-600">
                      <span>{t('booking.discount', { value: coupon.value })}</span>
                      <bdi className="font-medium">
                        {API.priceFormat(totalPrice(prices, true) - totalPrice(prices, false))}
                      </bdi>
                    </div>
                  ) : null}
                </div>

                {/* Total */}
                <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4">
                  <span className="font-display text-base font-semibold text-ink">
                    {t('booking.total')}
                  </span>
                  <bdi className="font-display text-xl font-bold text-brand-500">
                    {API.priceFormat(totalPrice(prices, true))}
                  </bdi>
                </div>

                {/* Coupon */}
                <div className="border-t border-slate-100 px-5 py-4">
                  {!addCoupon ? (
                    <button
                      type="button"
                      onClick={() => setAddCoupon(true)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 hover:underline"
                    >
                      <TagIcon className="h-4 w-4" />
                      {t('booking.addCoupon')}
                    </button>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder={t('booking.couponLabel')}
                          onChange={(e) => setCouponName(e.target.value)}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                        />
                        <button
                          type="button"
                          disabled={loadingCoupon || !couponName}
                          onClick={() => checkCoupon({ event_id: event.id, coupon_name: couponName })}
                          className="shrink-0 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
                        >
                          {loadingCoupon ? '…' : t('booking.couponApply')}
                        </button>
                      </div>
                      {couponHelper ? (
                        <p
                          className={`mt-2 text-xs ${
                            Object.keys(coupon).length ? 'text-emerald-600' : 'text-rose-500'
                          }`}
                        >
                          {couponHelper}
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Advance note — the API returns percentage_paid as a string
                    ("0.00"), so a strict !== 0 comparison was always true and
                    showed a "Deposit due: 0,00 MAD" banner even for
                    pay-in-full events. Gate on the actual computed deposit
                    amount instead. */}
                {advance > 0 && !loadingSettings ? (
                  <div className="border-t border-slate-100 bg-accent-50 px-5 py-3">
                    <p className="text-xs text-ink-muted">
                      {isFullOnlinePayment
                        ? t('booking.paymentNote', { amount: API.priceFormat(advance) })
                        : t('booking.advanceNote', { amount: API.priceFormat(advance) })}
                    </p>
                  </div>
                ) : null}

                {/* Confirm (desktop) */}
                <div className="hidden px-5 py-4 lg:block">
                  <button
                    type="button"
                    onClick={submitBooking}
                    disabled={handleDesableButton()}
                    className="w-full rounded-xl bg-accent-500 px-4 py-3.5 text-center text-sm font-bold text-brand-900 shadow-card transition-all duration-200 hover:bg-accent-400 hover:shadow-card-hover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-accent-500"
                  >
                    {t('booking.confirm')}
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky confirm bar. The floating WhatsApp button is nudged up
          (see below) so the CTA can run to the screen edge. */}
      <div className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3 shadow-elevated lg:hidden">
        <div>
          <p className="text-[11px] text-ink-subtle">{t('booking.total')}</p>
          <bdi className="font-display text-lg font-bold leading-none text-brand-500">
            {API.priceFormat(totalPrice(prices, true))}
          </bdi>
        </div>
        <button
          type="button"
          onClick={submitBooking}
          disabled={handleDesableButton()}
          className="flex-1 whitespace-nowrap rounded-xl bg-accent-500 px-4 py-3 text-center text-sm font-bold leading-none text-brand-900 shadow-card transition-colors hover:bg-accent-400 disabled:opacity-50"
        >
          {t('booking.confirm')}
        </button>
      </div>

      {/* TAF-34: inline confirm + auth (replaces the old /checkout page) */}
      <ConfirmAuthSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        orderBooking={orderBooking}
        gift={gift}
      />
    </div>
  );
};

// ---- presentational helpers -------------------------------------------------

function sejourDisable(date, event) {
  // `date` is a plain JS Date (from our DatePicker). If the stay has no fixed
  // window, don't disable anything.
  if (!event.date_depart || !event.date_end) return false;
  const d = moment(date);
  const startDate = moment(new Date(event.date_depart).toLocaleDateString('fr-FR'), 'DD/MM/yyyy');
  const endDate = moment(new Date(event.date_end).toLocaleDateString('fr-FR'), 'DD/MM/yyyy');
  return !(d.isSameOrAfter(startDate, 'day') && d.isSameOrBefore(endDate, 'day'));
}

function Field({ label, hint, icon: Icon, children }) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-ink">
        {Icon ? <Icon className="h-4 w-4 text-brand-500" /> : null}
        {label}
      </label>
      {hint ? <p className="mb-2 text-xs text-ink-subtle">{hint}</p> : null}
      {children}
    </div>
  );
}

function Stat({ label, value, danger }) {
  return (
    <div className="rounded-xl bg-surface-muted p-3 text-center">
      <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
      <p className={`mt-1 font-display text-sm font-bold ${danger ? 'text-rose-500' : 'text-ink'}`}>
        {value}
      </p>
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div className="flex items-center justify-between text-ink-muted">
      <span>{label}:</span>
      <span className="font-medium text-ink">{value}</span>
    </div>
  );
}

function PriceRow({ item, isSejour, onToggle, onQt, t }) {
  const disabledQt = !item.status || isSejour;
  const qt = parseInt(item.qt) || 0;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3.5">
      {/* checkbox */}
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={item.status}
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
          item.status ? 'border-brand-500 bg-brand-500 text-white' : 'border-slate-300 bg-white'
        }`}
      >
        {item.status ? <CheckIcon className="h-3.5 w-3.5" /> : null}
      </button>

      <div className="min-w-[40%] flex-1">
        <p className="text-sm font-medium text-ink">{item.name}</p>
        <p className="text-xs text-ink-subtle">{API.priceFormat(item.price)}</p>
      </div>

      {/* qty stepper */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={disabledQt || qt <= 0}
          onClick={(e) => onQt(e, Math.max(0, qt - 1))}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:opacity-40"
          aria-label="minus"
        >
          <MinusIcon className="h-4 w-4" />
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={item.qt}
          disabled={disabledQt}
          onChange={(e) => onQt(e)}
          className="w-12 appearance-none rounded-lg border border-slate-200 px-0 py-1.5 text-center text-sm text-ink [-moz-appearance:textfield] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 disabled:text-ink-subtle [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <button
          type="button"
          disabled={disabledQt}
          onClick={(e) => onQt(e, qt + 1)}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:opacity-40"
          aria-label="plus"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>

      {/* line total */}
      <div className="w-24 shrink-0 text-right rtl:text-left">
        <bdi className="text-sm font-semibold text-brand-500">
          {API.priceFormat(item.total_price)}
        </bdi>
      </div>
    </div>
  );
}

// Design-system dropdown built on Headless UI Listbox (fully styleable, RTL-aware).
function SelectBox({ value, onChange, options, placeholder, loading }) {
  const current = options.find((o) => String(o.value) === String(value));
  return (
    <Listbox value={value ?? ''} onChange={onChange} disabled={loading}>
      <div className="relative">
        <Listbox.Button
          className={cn(
            'flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-start text-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:opacity-60',
            current ? 'text-ink' : 'text-ink-subtle'
          )}
        >
          <span className="truncate">{loading ? '…' : current ? current.label : placeholder}</span>
          <ChevronUpDownIcon className="h-4 w-4 shrink-0 text-ink-subtle" />
        </Listbox.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-40 mt-2 max-h-60 w-full overflow-auto rounded-xl border border-slate-100 bg-white py-1.5 text-sm shadow-elevated focus:outline-none">
            {options.map((o) => (
              <Listbox.Option
                key={o.value}
                value={o.value}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none py-2.5 pl-9 pr-3 rtl:pl-3 rtl:pr-9',
                    active ? 'bg-brand-50 text-brand-700' : 'text-ink'
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span className={cn('block truncate', selected && 'font-semibold')}>{o.label}</span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-500 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3">
                        <CheckIcon className="h-4 w-4" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

export default Booking;
